import {
  isEmpty,
  get,
  cloneDeep,
  concat,
  flatten,
} from 'lodash-es'
import ItemUtils from './item-utils'

const nameMap = {
  DIVIDEND: 'Dividend',
  GIFTCARD: 'Gift card',
  PROMO_GCD: 'Gift card',
  MERCH_CREDIT: 'Gift card',
  GIFTCERT: 'Gift card',
}

class KlarnaUtils {
  static sanitizeName(name = '') {
    return name.replace(/[^a-zA-Z0-9\-\s]+/g, ' ');
  }  

  static mapToKlarnaLineItems(lineItems) {
    // Example Item
    // order_lines[]
    // order_lines.type
    // order_lines.name
    // order_lines.reference
    // order_lines.image_url
    // order_lines.product_url
    // order_lines.quantity
    // order_lines.unit_price
    // order_lines.total_amount

    const mappedLineItems = lineItems.map(item => ({
      type: ItemUtils.isGiftCard(item) ? 'gift_card' : 'physical',
      image_url: `${window.location.origin}/media/product/${item.sku}?size=200`,
      product_url: `${window.location.origin}/product/${item.sku}`,
      name: (item.description === 'GIFT CERTIFICATE') ? 'One time bonus card' : item.description,
      reference: item.sku,
      quantity: item.quantity,
      unit_price: Math.round(item.price.unit * 100),
      total_amount: Math.round(item.price.totalAdjusted * 100),
      total_discount_amount: Math.round(item.price.totalDiscount * 100),
      // total_tax_amount - N/A
      // tax_rate - N/A
    }))

    return mappedLineItems
  }

  static constructKlarnaBilling(orderBillingAddress) {
    const klarnaBilling = {
      given_name: KlarnaUtils.sanitizeName(orderBillingAddress.firstName),
      family_name: KlarnaUtils.sanitizeName(orderBillingAddress.lastName),
      // organization_name: "string",
      email: orderBillingAddress.email,
      title: orderBillingAddress.prefix,
      street_address: orderBillingAddress.line1,
      street_address2: orderBillingAddress.line2,
      // street_name: "string",
      // street_number: "string",
      // house_extension: "string",
      postal_code: orderBillingAddress.postalCode,
      city: orderBillingAddress.city,
      region: orderBillingAddress.stateCode,
      phone: orderBillingAddress.phoneNumber,
      country: orderBillingAddress.countryCode,
      // care_of: "string",
      // reference: "string",
      // attention: "string",
    }

    return klarnaBilling
  }

  static constructKlarnaShipping(orderShippingAddress, orderBillingAddress) {
    if (isEmpty(orderShippingAddress)) return KlarnaUtils.constructKlarnaBilling(orderBillingAddress)

    let storeName = 'REI'
    if (get(orderShippingAddress, 'storeDisplayName')) {
      storeName = `${orderShippingAddress.storeDisplayName} - ${orderShippingAddress.storeId}`
    }

    const klarnaShipping = {
      given_name: KlarnaUtils.sanitizeName(orderShippingAddress.firstName),
      family_name: KlarnaUtils.sanitizeName(orderShippingAddress.lastName || storeName || orderShippingAddress.firstName),
      // organization_name: "string",
      email: orderBillingAddress.email,
      title: orderShippingAddress.prefix,
      street_address: orderShippingAddress.line1,
      street_address2: orderShippingAddress.line2,
      // street_name: "string",
      // street_number: "string",
      // house_extension: "string",
      postal_code: orderShippingAddress.postalCode,
      city: orderShippingAddress.city,
      region: orderShippingAddress.state,
      phone: orderBillingAddress.phone,
      country: orderShippingAddress.countryCode,
      // care_of: "string",
      // reference: "string",
      // attention: "string",
    }

    return klarnaShipping
  }


  /**
   * Types of Klarna line items
   *
   * physical (physical good)
   * discount
   * shipping_fee
   * sales_tax (depends on the country/city, usually called VAT)
   * digital (digital good)
   * gift_card
   * store_credit (credit from the merchant)
   * surcharge (extra charge)
   */

  static constructSplitTenders(orderModel) {
    // For each attached tender, add it to the Klarna payload
    const orderTenders = orderModel.safeGet('tenders')
    const attachedTenders = Array.isArray(orderTenders) ? cloneDeep(orderTenders) : []

    // remove all tenders except for gift cards
    // and dividend from this tender list
    attachedTenders.forEach((tender, ind) => {
      if (!ItemUtils.isPhysicalGiftCard(tender)
        && !ItemUtils.isDigitalGiftCard(tender)
        && !ItemUtils.isPromoGiftCard(tender)
        && !(tender.type === 'DIVIDEND')) {
        // remove the tender =>
        attachedTenders.splice(ind, 1)
      }
    })

    const tendersMap = attachedTenders.reduce((acc, tender) => {
      if (tender.amount > 0) {
        acc.push({
          name: nameMap[tender.type] || 'Gift card',
          type: 'gift_card',
          quantity: 1,
          unit_price: -Math.round((tender.amount || 0) * 100),
          total_amount: -Math.round((tender.amount || 0) * 100),
          total_discount_amount: 0,
        })
      }

      return acc
    }, [])

    return tendersMap || {}
  }

  static constructBopusItem(orderModel) {
    const pickupItems = orderModel.safeGet('getters.filteredPickupLineItems') || {}
    if (pickupItems.length > 0) {
      return {
        name: 'In-store pickup',
        quantity: 1,
        total_amount: 0,
        type: 'shipping_fee',
        unit_price: 0,
      }
    }
    return {}
  }

  static constructSalesTax(orderModel) {
    // Deal with sales tax as a "line item" per Klarna rep
    return {
      name: 'Sales Tax',
      type: 'sales_tax',
      quantity: 1,
      unit_price: Math.round((orderModel.safeGet('getters.taxTotal') || 0) * 100),
      total_amount: Math.round((orderModel.safeGet('getters.taxTotal') || 0) * 100),
    }
  }

  static constructShippingFee(orderModel) {
    const shippingFee = orderModel.safeGet('getters.getMerchandiseShipCharges') || 0
    return {
      name: 'Shipping Fee',
      type: 'shipping_fee',
      quantity: 1,
      unit_price: Math.round(shippingFee * 100),
      total_amount: Math.round(shippingFee * 100),
    }
  }

  static constructOversizedFee(orderModel) {
    const oversizedShippingFee = orderModel.safeGet('totalPrice.totalOversizedChargeAdjusted') || 0
    if (oversizedShippingFee > 0) {
      return {
        name: 'Oversized Shipping Fee',
        type: 'shipping_fee',
        quantity: 1,
        unit_price: Math.round(oversizedShippingFee * 100),
        total_amount: Math.round(oversizedShippingFee * 100),
      }
    }
    return {}
  }

  static constructKlarnaOrderData(orderModel) {
    const orderBilling = orderModel.safeGet('getters.currentBillingAddress') || {}
    const klarnaBilling = KlarnaUtils.constructKlarnaBilling(orderBilling)

    const pickupItems = orderModel.safeGet('getters.filteredPickupLineItems') || {}
    const orderShipping = orderModel.safeGet('getters.determineShippingAddress') || {}

    let klarnaShipping = {}
    if (pickupItems.length > 0) klarnaShipping = KlarnaUtils.constructKlarnaShipping(pickupItems[0].shippingAddress.address, orderBilling)
    else klarnaShipping = KlarnaUtils.constructKlarnaShipping(orderShipping.address, orderBilling)

    const lineItems = orderModel.safeGet('lineItems')
    const mappedLineItems = KlarnaUtils.mapToKlarnaLineItems(lineItems)

    const splitTendersItem = KlarnaUtils.constructSplitTenders(orderModel)
    const bopusItem = KlarnaUtils.constructBopusItem(orderModel)
    const taxItem = KlarnaUtils.constructSalesTax(orderModel)
    const shippingFee = KlarnaUtils.constructShippingFee(orderModel)
    const oversizedFee = KlarnaUtils.constructOversizedFee(orderModel)

    const additionalOrderLines = []
    if (!isEmpty(splitTendersItem)) additionalOrderLines.push(splitTendersItem)
    if (!isEmpty(bopusItem)) additionalOrderLines.push(bopusItem)
    if (!isEmpty(taxItem)) additionalOrderLines.push(taxItem)
    if (!isEmpty(shippingFee)) additionalOrderLines.push(shippingFee)
    if (!isEmpty(oversizedFee)) additionalOrderLines.push(oversizedFee)

    const klarnaOrderLines = flatten(concat(mappedLineItems, additionalOrderLines))

    return {
      merchant_reference1: orderModel.safeGet('externalOrderId'),
      billing_address: klarnaBilling,
      shipping_address: klarnaShipping,
      purchase_country: klarnaBilling.country,
      purchase_currency: 'USD',
      order_amount: Math.round(orderModel.safeGet('balance', 0) * 100),
      order_tax_amount: Math.round((orderModel.safeGet('getters.taxTotal') || 0) * 100),
      order_lines: klarnaOrderLines,
    }
  }
}

export default KlarnaUtils
